import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";
import { SectionClouds } from "@/components/landing";
import { CtaBand } from "@/components/CtaBand";
import { EngineDiagram } from "./EngineDiagram";
import andreaImg from "@/assets/andrea-2.jpg";
import edwinImg from "@/assets/edwin-2.jpg";
import evertImg from "@/assets/evert-2.jpg";

type MemberId = "andrea" | "edwin" | "evert";

const MEMBERS: Array<{ id: MemberId; photo: string; linkedin: string }> = [
  {
    id: "andrea",
    photo: andreaImg,
    linkedin: "https://www.linkedin.com/in/andreacabral/",
  },
  {
    id: "edwin",
    photo: edwinImg,
    linkedin: "https://www.linkedin.com/in/edwinwesterhoud/",
  },
  {
    id: "evert",
    photo: evertImg,
    linkedin: "https://www.linkedin.com/in/evert-rotshuizen-2637a15b/",
  },
];

interface FactItem {
  label: string;
  value: string;
}

interface TimelineItem {
  year: string;
  text: string;
}

/** Shared reveal-on-scroll pattern used across the landing sections. */
function useReveal<T extends HTMLElement>() {
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<T>(null);
  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, isVisible };
}

const reveal = (isVisible: boolean, delay = 0) => ({
  className: cn(
    "transition-all duration-700",
    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6",
  ),
  style: { transitionDelay: isVisible ? `${delay}ms` : "0ms" },
});

export function AboutContent() {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const facts = t("about.facts.items", { returnObjects: true }) as FactItem[];
  const timeline = t("about.story.timeline", {
    returnObjects: true,
  }) as TimelineItem[];

  const { ref: heroRef, isVisible: heroVisible } = useReveal<HTMLElement>();
  const { ref: storyRef, isVisible: storyVisible } = useReveal<HTMLElement>();
  const { ref: foundersRef, isVisible: foundersVisible } = useReveal<HTMLElement>();
  const { ref: edgeRef, isVisible: edgeVisible } = useReveal<HTMLElement>();

  return (
    <>
      {/* Hero: pitch left, plain facts right */}
      <section ref={heroRef} className="frame bg-page-bg">
        <SectionClouds variant="case-study" />
        <div className="container-narrow section-pad relative z-10 !pt-10">
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
            <div {...reveal(heroVisible)}>
              <span className="eyebrow">{t("about.eyebrow")}</span>
              <h1 className="mt-4 text-[clamp(36px,5.6vw,68px)] leading-[1.04] font-medium tracking-[-0.035em] text-ink">
                <Trans
                  i18nKey="about.title"
                  components={{
                    italic: <span className="serif-italic text-accent" />,
                  }}
                />
              </h1>
              <p className="mt-6 max-w-[52ch] text-[clamp(15px,1.4vw,17px)] leading-[1.55] text-ink-2">
                {t("about.lede")}
              </p>
            </div>

            <div
              {...reveal(heroVisible, 120)}
              className={cn(
                reveal(heroVisible, 120).className,
                "rounded-[22px] border border-line bg-surface p-7 lg:mt-9",
              )}
            >
              <div className="mono text-[11px] tracking-[0.16em] text-accent uppercase">
                {t("about.facts.eyebrow")}
              </div>
              <dl className="mt-4 divide-y divide-line">
                {facts.map((f) => (
                  <div key={f.label} className="flex items-baseline justify-between gap-6 py-3">
                    <dt className="mono text-[11px] tracking-[0.14em] whitespace-nowrap text-ink-3 uppercase">
                      {f.label}
                    </dt>
                    <dd className="text-right text-[14px] font-medium text-ink">{f.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* 01 — Founders */}
      <section ref={foundersRef} className="frame bg-surface-2/40">
        <SectionClouds variant="team" />
        <div className="container-narrow section-pad relative z-10">
          <div {...reveal(foundersVisible)}>
            <span className="eyebrow">
              <span className="num">01 —</span> {t("about.team.eyebrow")}
            </span>
            <h2 className="section-title">
              {t("about.team.titleLead")}{" "}
              <span className="serif-italic">{t("about.team.titleEm")}</span>
            </h2>
            <p className="section-lede">{t("about.team.lede")}</p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3 lg:gap-7">
            {MEMBERS.map((m, i) => (
              <div
                key={m.id}
                {...reveal(foundersVisible, i * 80)}
                className={cn(
                  reveal(foundersVisible, i * 80).className,
                  "lift flex flex-col rounded-[22px] border border-line bg-surface p-7",
                )}
              >
                <div className="aspect-[1.1] overflow-hidden rounded-[16px] bg-surface-2">
                  <img
                    src={m.photo}
                    alt={t(`about.team.members.${m.id}.name`)}
                    loading="lazy"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="mt-5 flex flex-1 flex-col">
                  <div className="text-[19px] leading-tight font-medium text-ink">
                    {t(`about.team.members.${m.id}.name`)}
                  </div>
                  <div className="mono mt-1 text-[11px] tracking-[0.14em] text-accent uppercase">
                    {t(`about.team.members.${m.id}.role`)}
                  </div>
                  <p className="mt-3 text-[13.5px] leading-[1.6] text-ink-2">
                    {t(`about.team.members.${m.id}.bio`)}
                  </p>
                  <a
                    href={m.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto inline-flex items-center gap-1 self-start pt-4 text-[13px] font-medium text-ink transition-colors hover:text-accent"
                  >
                    {t("about.team.linkedin")} <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 02 — Story + timeline */}
      <section ref={storyRef} className="frame bg-page-bg">
        <div className="container-narrow section-pad relative z-10">
          <div {...reveal(storyVisible)}>
            <span className="eyebrow">
              <span className="num">02 —</span> {t("about.story.eyebrow")}
            </span>
            <h2 className="section-title">
              {t("about.story.titleLead")}{" "}
              <span className="serif-italic">{t("about.story.titleEm")}</span>
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-x-14 gap-y-5 text-[16px] leading-[1.65] text-ink-2 lg:grid-cols-2">
            <p {...reveal(storyVisible, 60)}>{t("about.story.p1")}</p>
            <p {...reveal(storyVisible, 120)}>{t("about.story.p2")}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {timeline.map((item, i) => (
              <div
                key={item.year}
                {...reveal(storyVisible, 200 + i * 80)}
                className={cn(
                  reveal(storyVisible, 200 + i * 80).className,
                  "lift rounded-[18px] border border-line bg-surface p-6",
                )}
              >
                <div className="mono text-[12px] tracking-[0.14em] text-accent">{item.year}</div>
                <div aria-hidden="true" className="mt-3 h-px w-6 bg-accent/60" />
                <p className="mt-3 text-[13.5px] leading-[1.55] text-ink-2">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 03 — Our edge: sell the system, then the small print */}
      <section ref={edgeRef} className="frame bg-surface-2/40">
        <SectionClouds variant="faq" />
        <div className="container-narrow section-pad relative z-10">
          <div {...reveal(edgeVisible)}>
            <span className="eyebrow">
              <span className="num">03 —</span> {t("about.tooling.eyebrow")}
            </span>
            <h2 className="section-title">
              {t("about.tooling.titleLead")}{" "}
              <span className="serif-italic">{t("about.tooling.titleEm")}</span>
            </h2>
          </div>
          <p
            {...reveal(edgeVisible, 80)}
            className={cn(
              reveal(edgeVisible, 80).className,
              "mt-7 max-w-[68ch] text-[16px] leading-[1.65] text-ink-2",
            )}
          >
            {t("about.tooling.p1")}
          </p>
          <Link
            to={localizedHref("/process")}
            className="mt-5 inline-flex text-[14px] font-medium text-accent transition-colors hover:text-ink"
          >
            {t("about.tooling.diagram.link")}
          </Link>
          <div
            {...reveal(edgeVisible, 160)}
            className={cn(reveal(edgeVisible, 160).className, "mt-10 lg:mt-12")}
          >
            <EngineDiagram />
          </div>
        </div>
      </section>

      <CtaBand
        titleLead={t("about.cta.titleLead")}
        rotateWords={t("about.cta.words", { returnObjects: true }) as string[]}
        bookLabel={t("about.cta.bookLink")}
        secondaryLabel={t("about.cta.processLink")}
        secondaryTo="/process"
        ghostLabel={t("about.cta.workLink")}
        ghostTo="/work"
        trackSource="about"
      />
    </>
  );
}
